import React from 'react';
import Select, { ActionMeta } from 'react-select';

import classnames from 'classnames';

import { Modifier } from 'src/apollo/onlineOrdering';
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice';

import { useModifierContext } from 'public/components/online_ordering/ModifierContext';
import { ModifierGroup } from 'public/components/online_ordering/types';

export const ModifiersSection = ({ groups }: { groups: ModifierGroup[] }) => {
  const { registerModifierGroup, isInvalid } = useModifierContext();

  return (
    <div data-testid="modifierGroups">
      {groups?.map((group: ModifierGroup) => {
        const { guid, name, minSelections, maxSelections } = group;
        const required = Boolean(minSelections && minSelections > 0);

        return (
          <div key={guid} className="modifierGroupRow" {...registerModifierGroup(guid)} data-testid={`modifierGroup-${group.guid}`}>
            <label>
              <label htmlFor={guid}>
                <h3>{name}</h3>
              </label>

              {required &&
                <>
                  <div className={classnames('required', { highlight: isInvalid(guid) })}>
                    Required*
                  </div>
                </>}

              <div className="modifierGroupSubtitle">
                {maxSelections && `Please select ${minSelections === maxSelections ? '' : minSelections ? `${minSelections} to ` : 'up to '}${maxSelections}`}
              </div>

              <ModifierSelect group={group} />
            </label>
          </div>);
      })}
    </div>
  );
};

type OptionType = {
  value: Modifier;
  label: React.ReactNode;
}

const ModifierSelect = ({ group }: { group: ModifierGroup }) => {
  const { displayedModifier, select, deselect, modifierGroupData } = useModifierContext();
  const { guid, modifiers, minSelections, maxSelections } = group;
  const required = Boolean(minSelections && minSelections > 0);

  // eslint-disable-next-line no-extra-parens
  const options: OptionType[] = (modifiers as Modifier[]).map(modifier => {
    return {
      value: modifier,
      label: modifier.name || ''
    };
  });
  const selectedValues = options.filter(option => {
    const selectedItem = displayedModifier?.modifierGroups[group.guid]?.[option.value.itemGuid];
    return !!selectedItem;
  });

  const formatOptionLabel = ({ value: modifier }: OptionType) => {
    const selectedItem = displayedModifier?.modifierGroups[guid]?.[modifier.itemGuid];

    const isIncludedDefault = modifier.isDefault && modifierGroupData[guid]?.defaultOptionsChargePrice === 'NO';
    const shouldShowPrice = modifier.price && modifier.price !== 0 && (!isIncludedDefault || selectedItem && selectedItem.quantity > 1);
    const quantity = selectedItem?.quantity || 1;

    return (
      <div className="modifierTextContent" key={modifier.itemGuid}>
        <div className="modifierText">
          <div>{modifier.name}</div>
        </div>
        {modifier.price && shouldShowPrice ?
          <span className={'price'}>
            {modifier.price > 0 ? '+' : '-'}<FormattedPrice value={(isIncludedDefault ? quantity - 1 : quantity) * Math.abs(modifier.price)} />
          </span>
          : null}
      </div>
    );
  };

  const onChange = (selections: OptionType | OptionType[], actionMeta: ActionMeta<OptionType>) => {
    switch(actionMeta.action) {
      case 'select-option': {
        const modifier: Modifier | null | undefined =
            !Array.isArray(selections) ? selections?.value : !Array.isArray(actionMeta.option) ? actionMeta.option?.value : null;
        if(modifier) {
          select(guid, modifier.itemGuid, 1, actionMeta.option ? 'merge' : 'replace', modifier.itemGroupGuid, modifier.name || undefined, modifier.price, modifier.isDefault);
        }
        break;
      }
      case 'pop-value':
      case 'remove-value': {
        deselect(guid, actionMeta.removedValue.value.itemGuid);
        break;
      }
      case 'clear': {
        const modifiersToClear = actionMeta.removedValues;
        modifiersToClear.forEach(modifier => deselect(guid, modifier.value.itemGuid));
        break;
      }
    }
  };

  return (
    <Select
      inputId={guid}
      classNamePrefix="react-select"
      isClearable={!required}
      isSearchable={false}
      isMulti={!maxSelections}
      required={required}
      menuPosition="fixed"
      value={selectedValues}
      options={options}
      formatOptionLabel={formatOptionLabel}
      onChange={onChange} />
  );
};
